/**
 * Ondia consent-laag. Eén script-tag per site, in de <head> vóór het
 * GTM-snippet, zonder defer of async:
 *
 *   <script src="https://consent.ondia.be/ondia-consent.js" data-property="support"></script>
 *
 * Doet drie dingen:
 *   1. Consent Mode v2 default (alles denied) in de dataLayer, vóór GTM laadt
 *   2. Eigen CSS injecteren en de banner tonen zodra de DOM er is
 *   3. Bij een keuze gtag('consent','update') sturen; de tags in GTM reageren
 *      via hun Consent Settings
 *
 * Opties via data-attributen op de script-tag, of via window.ONDIA_CONSENT
 * (dat wint): data-property (verplicht), data-locale (standaard 'nl').
 * Exporteert window.OndiaConsent voor eigen code.
 */

import * as CookieConsent from 'vanilla-cookieconsent';

import { REVISION, cookieDomain, COOKIE_NAME, LOCALES, categoriesFor } from './config.js';
import nl from '../public/i18n/nl.json';
import css from './theme.css';

const PREFERENCES_SELECTOR = '[data-ondia-consent="preferences"]';
const RESET_SELECTOR = '[data-ondia-consent="reset"]';
const BUNDLES = { nl };

// ---------------------------------------------------------------------------
// 1. Google Consent Mode v2
// ---------------------------------------------------------------------------

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const CONSENT_DEFAULT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
  // GTM wacht tot 500 ms op de update uit de opgeslagen keuze.
  wait_for_update: 500,
};

/** Categorie in de banner -> Consent Mode-signalen. */
const SIGNAL_MAP = {
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
};

/** Alles denied, idempotent. Waarschuwt als GTM al geladen was: dan is het te laat. */
function setGoogleConsentDefault() {
  const dl = window.dataLayer || [];
  if (dl.some((e) => e && e[0] === 'consent' && e[1] === 'default')) return;

  if (window.google_tag_manager) {
    console.warn(
      '[ondia-consent] GTM was al geladen vóór de consent-laag. Zet de ' +
        'consent-script-tag vóór het GTM-snippet, zonder defer of async.'
    );
  }
  gtag('consent', 'default', CONSENT_DEFAULT);
}

function updateGoogleConsent(acceptedCategories) {
  const state = { security_storage: 'granted' };
  for (const [category, signals] of Object.entries(SIGNAL_MAP)) {
    const granted = acceptedCategories.includes(category) ? 'granted' : 'denied';
    for (const signal of signals) state[signal] = granted;
  }
  gtag('consent', 'update', state);

  // Alternatieve trigger in GTM: Custom Event "ondia_consent_update".
  window.dataLayer.push({
    event: 'ondia_consent_update',
    ondia_consent_categories: acceptedCategories.join(','),
  });
}

// ---------------------------------------------------------------------------
// 2. Vertaling: nl.json + cookietabel van de property
// ---------------------------------------------------------------------------

function buildTranslation(locale, property) {
  const raw = BUNDLES[locale];
  if (!raw) throw new Error(`[ondia-consent] Geen vertaling voor "${locale}".`);

  // Diepe kopie: de geïmporteerde JSON is gedeeld en mag niet gemuteerd worden.
  const t = JSON.parse(JSON.stringify(raw));

  // Bewust geen cookietabellen in het paneel: alleen titel, uitleg en schakelaar.
  // De inventaris in cookie-tables.js blijft de bron voor autoClear en onderhoud.

  // Helper-velden horen niet in het translation-object dat de plugin ziet.
  delete t.cookieTableHeaders;
  delete t.cookiePurposes;
  delete t.durations;
  delete t.cookieTableCaption;
  return t;
}

function translationsFor(locales, property) {
  const translations = {};
  for (const locale of locales) translations[locale] = buildTranslation(locale, property);
  return translations;
}

// ---------------------------------------------------------------------------
// 3. Banner
// ---------------------------------------------------------------------------

let initialised = false;

/**
 * @param {object}  options
 * @param {string}  options.property        'marketing-site' | 'app' | 'platform' | 'support'
 * @param {string} [options.defaultLocale]  standaard 'nl'
 * @param {boolean}[options.hideFromBots]   standaard true; in e2e-tests false, anders
 *                                          ziet Playwright (navigator.webdriver) nooit een banner
 */
function initOndiaConsent(options = {}) {
  if (initialised) return CookieConsent;
  initialised = true;

  const { property = 'unknown', defaultLocale = 'nl', hideFromBots = true } = options;

  const announce = (cookie, event) => {
    const categories = cookie.categories || [];
    updateGoogleConsent(categories);
    // Eigen event voor code die niet van de library-internals wil afhangen.
    window.dispatchEvent(
      new CustomEvent('ondia:consent', { detail: { event, categories, property } })
    );
  };

  CookieConsent.run({
    // 'opt-in': niets vuurt zonder toestemming. Niet wijzigen.
    mode: 'opt-in',
    revision: REVISION,

    // Onderschept <script type="text/plain" data-category="..."> en activeert die pas na toestemming.
    manageScriptTags: true,
    autoClearCookies: true,
    autoShow: true,
    hideFromBots,

    // Bewust false: een blokkerende overlay komt in de buurt van een cookiewall,
    // en die zijn volgens de GBA niet toegestaan.
    disablePageInteraction: false,

    cookie: {
      name: COOKIE_NAME,
      domain: cookieDomain(),
      path: '/',
      sameSite: 'Lax',
      // Alleen over https. Op een lokale http://*.test-site weigert de browser
      // een Secure-cookie stil, en dan komt de banner bij elke pageload terug.
      secure: window.location.protocol === 'https:',
      // 12 maanden, bewust boven de 6 die GBA/CNIL aanraden. Vaste termijn,
      // ongeacht de keuze. Geen langere bewaring als beloning
      // voor "alles accepteren": dat merkt de GBA aan als deceptive design.
      expiresAfterDays: 365,
    },

    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        // Weigeren staat visueel gelijk aan accepteren. GBA-eis. Niet wijzigen.
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: categoriesFor(property),

    language: {
      default: defaultLocale,
      // Alleen NL, geen browserdetectie.
      translations: translationsFor(LOCALES, property),
    },

    onConsent: ({ cookie }) => announce(cookie, 'consent'),
    onChange: ({ cookie }) => announce(cookie, 'change'),
  });

  return CookieConsent;
}

/**
 * Alles wissen en opnieuw vragen. Voor een "Cookies verwijderen"-link in de footer.
 *
 *   1. Alles weigeren: autoClear ruimt de tracker-cookies op en GTM krijgt
 *      'denied' via Consent Mode.
 *   2. Inventaris nogmaals wissen, voor cookies die buiten de categorie-
 *      wijziging vallen (bv. als er al geen toestemming was).
 *   3. Consent-cookie weg, modals en listeners opruimen, verse banner.
 *
 * Omdat de cookie op .ondia.be staat, reset dit meteen alle *.ondia.be-sites.
 * Third-party cookies (doubleclick.net, linkedin.com) kan JS niet wissen;
 * die zet GTM niet meer zodra de status 'denied' is.
 */
function resetOndiaConsent() {
  if (!initialised) return;

  CookieConsent.acceptCategory([]);
  // Zonder domein-argument probeert de plugin host-only én het ingestelde domein.
  CookieConsent.eraseCookies([/^_ga/, '_gid', /^_gcl/, '_fbp', 'li_sugr', 'bcookie', /^ph_/], '/');
  CookieConsent.reset(true);

  initialised = false;
  window.dispatchEvent(
    new CustomEvent('ondia:consent', {
      detail: { event: 'reset', categories: [], property: options.property },
    })
  );
  initOndiaConsent(options);
}

// Footer-links. Gedelegeerd, dus ook voor elementen die later in de DOM komen,
// en één keer geregistreerd zodat een reset geen dubbele listeners oplevert.
//   data-ondia-consent="preferences"  opent het voorkeurenpaneel
//   data-ondia-consent="reset"        wist alles en toont de banner opnieuw
document.addEventListener('click', (event) => {
  const closest = (selector) => event.target.closest?.(selector);
  if (closest(PREFERENCES_SELECTOR)) {
    event.preventDefault();
    CookieConsent.showPreferences();
  } else if (closest(RESET_SELECTOR)) {
    event.preventDefault();
    resetOndiaConsent();
  }
});

// ---------------------------------------------------------------------------
// Auto-init
// ---------------------------------------------------------------------------

setGoogleConsentDefault();

function readOptions() {
  const fromGlobal = window.ONDIA_CONSENT || {};
  const script =
    document.currentScript ||
    document.querySelector('script[data-property][src*="ondia-consent"]');

  if (script && (script.defer || script.async)) {
    console.warn(
      '[ondia-consent] Script-tag heeft defer/async. Haal dat weg, anders ' +
        'laadt GTM vóór de Consent Mode default.'
    );
  }

  const fromAttrs = script
    ? { property: script.dataset.property, defaultLocale: script.dataset.locale }
    : {};

  // undefined-waarden weglaten, anders overschrijven ze de defaults.
  const merged = { ...fromAttrs, ...fromGlobal };
  for (const key of Object.keys(merged)) {
    if (merged[key] === undefined) delete merged[key];
  }
  return merged;
}

const options = readOptions();
if (!options.property) {
  console.warn(
    '[ondia-consent] Geen property opgegeven. Zet data-property op de script-tag. ' +
      'Cookietabellen en services blijven nu leeg.'
  );
}

if (!document.querySelector('style[data-ondia-consent]')) {
  const style = document.createElement('style');
  style.setAttribute('data-ondia-consent', 'theme');
  style.textContent = css;
  document.head.appendChild(style);
}

window.OndiaConsent = {
  init: initOndiaConsent,
  hasConsent: (category) => CookieConsent.acceptedCategory(category),
  showPreferences: () => CookieConsent.showPreferences(),
  reset: resetOndiaConsent,
  CookieConsent,
};

const start = () => initOndiaConsent(options);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
